import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { formatSecondsToHMS, formatTaiwanDate } from "../TimeHandeler/TimeHandeler";
import { OrderDataColums, RecordDataColums } from "../../constants";

export function exportOrderCSV(rows, columns) {
    const columnNameMap = {
        id: "訂單編號",
        name: "姓名",
        phone: "手機號碼",
        email: "電子郵件",
        deliveryDate: "送貨日期",
        addressCity: "縣市",
        addressDistrict: "區",
        addressDetailed: "詳細地址",
        furnitureCount: "家具數量",
        isShipped: "寄送狀態",
        status: "審核狀態",
    };

    if (!rows || rows.length === 0) {
        alert("沒有可匯出的資料");
        return;
    }

    // 準備標題列
    const headers = Object.keys(columnNameMap)
        .filter((colKey) => columns.includes(colKey))
        .map((colKey) => columnNameMap[colKey]);

    // 準備資料列
    const data = rows.map((item) =>
        Object.keys(columnNameMap)
            .filter((colKey) => columns.includes(colKey))
            .map((colKey) => {
                if (colKey === "id") {
                    return `${item[colKey]}`;
                } else if (colKey === "isShipped") {
                    return item[colKey] ? "已送達" : "未送達";
                } else if (colKey === "status") {
                    return item[colKey] === 1 ? "已通過" : item[colKey] === 2 ? "未通過" : "未審核";
                } else if (colKey === "deliveryDate") {
                    return item[colKey] ? `${formatTaiwanDate(item[colKey])}` : "";
                } else if (colKey === "phone") {
                    return item[colKey] ? `'${String(item[colKey])}` : "";
                } else {
                    return item[colKey] ?? "";
                }
            })
    );

    // 用 aoa (array of array) 建立 worksheet，避免 XLSX 自動判斷型別
    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...data]);

    const csvOutput = XLSX.utils.sheet_to_csv(worksheet, { FS: "," });

    const blob = new Blob(["\uFEFF" + csvOutput], {
        type: "text/csv;charset=utf-8;",
    });
    saveAs(blob, `訂單資料_${new Date().toISOString().slice(0, 10)}.csv`);
}

export function exportOrderExcel(rows, columns) {
    let columnNameMap = {};

    OrderDataColums.forEach(col => {
        // 只取有 key 和 name 的欄位
        if (col.key && col.name) {
            columnNameMap[col.key] = col.name;
        }
    });

    if (!rows || rows.length === 0) {
        alert("沒有可匯出的資料");
        return;
    }

    // 準備標題列
    const headers = Object.keys(columnNameMap)
        .filter((colKey) => columns.includes(colKey))
        .map((colKey) => columnNameMap[colKey]);

    // 準備資料列
    const data = rows.map((item) =>
        Object.keys(columnNameMap)
            .filter((colKey) => columns.includes(colKey))
            .map((colKey) => {
                if (colKey === "id") {
                    return item[colKey];
                } else if (colKey === "isShipped") {
                    return item[colKey] ? "已送達" : "未送達";
                } else if (colKey === "status") {
                    return item[colKey] === 1 ? "已通過" : item[colKey] === 2 ? "未通過" : "未審核";
                } else if (colKey === "deliveryDate") {
                    return item[colKey] ? formatTaiwanDate(item[colKey]) : "";
                } else if (colKey === "phone") {
                    return item[colKey] ? String(item[colKey]) : "";
                } else if (colKey === "hasFloorService") {
                    return item[colKey] ? "是" : "否";
                } else {
                    return item[colKey] ?? "";
                }
            })
    );

    // 建立 worksheet
    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...data]);

    const phoneColIndex = headers.indexOf("手機號碼");
    if (phoneColIndex !== -1) {
        for (let row = 1; row <= data.length; row++) {
            const cellAddress = XLSX.utils.encode_cell({ r: row, c: phoneColIndex });
            if (worksheet[cellAddress]) {
                worksheet[cellAddress].t = "s"; // 強制字串
            }
        }
    }

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "訂單資料");

    // 匯出 Excel
    XLSX.writeFile(workbook, `訂單資料_${new Date().toISOString().slice(0, 10)}.xlsx`);
}

export function exportDeliveryRecordExcel(rows, columns) {
    let columnNameMap = {};

    RecordDataColums.forEach(col => {
        // 只取有 key 和 name 的欄位
        if (col.key && col.name) {
            columnNameMap[col.key] = col.name;
        }
    });

    if (!rows || rows.length === 0) {
        alert("沒有可匯出的資料");
        return;
    }

    // 準備標題列
    const headers = Object.keys(columnNameMap)
        .filter((colKey) => columns.includes(colKey))
        .map((colKey) => columnNameMap[colKey]);

    // 準備資料列
    const data = rows.map((item) =>
        Object.keys(columnNameMap)
            .filter((colKey) => columns.includes(colKey))
            .map((colKey) => {
                if (colKey === "id") {
                    return item[colKey];
                } else if (colKey === "status") {
                    return item[colKey] ? "已完成" : "待完成";
                } else if (colKey === "deliveryDate") {
                    return item[colKey] ? formatTaiwanDate(item[colKey]) : "";
                } else if (colKey === "phone") {
                    return item[colKey] ? String(item[colKey]) : "";
                } else if (colKey === "drivingTime") {
                    return item[colKey] ? formatSecondsToHMS(item[colKey]) : "";
                } else if (colKey === "weight") {
                    return item[colKey] ? item[colKey] + "kg" : "";
                } else if (colKey === "longDrivingKm") {
                    return item[colKey] ? item[colKey] + "km" : "";
                } else if (colKey === "orderId") {
                    return item[colKey] ? item[colKey].map(order => `${order}`).join(" | ") : "";
                } else {
                    return item[colKey] ?? "";
                }
            })
    );

    // 建立 worksheet
    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...data]);

    const phoneColIndex = headers.indexOf("手機號碼");
    if (phoneColIndex !== -1) {
        for (let row = 1; row <= data.length; row++) {
            const cellAddress = XLSX.utils.encode_cell({ r: row, c: phoneColIndex });
            if (worksheet[cellAddress]) {
                worksheet[cellAddress].t = "s"; // 強制字串
            }
        }
    }

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "指派紀錄資料");

    // 匯出 Excel
    XLSX.writeFile(workbook, `指派紀錄資料_${new Date().toISOString().slice(0, 10)}.xlsx`);
}